import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2, CalendarPlus } from "lucide-react";

import { fmtMoney } from "../../lib/currency";

function fmtTRY(n) {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("tr-TR");
}

export default function MasterSubscriptions() {
  const [list, setList] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [planId, setPlanId] = useState("");
  const [periodOverride, setPeriodOverride] = useState("");
  // "Süre Ata" — Paket Ata'dan BAĞIMSIZ: yalnızca bitiş tarihine gün ekler.
  const [extending, setExtending] = useState(null);
  const [extendDays, setExtendDays] = useState("");
  const [extendSaving, setExtendSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/master/subscriptions"),
        api.get("/master/plans"),
      ]);
      setList(s.data);
      setPlans(p.data);
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setEditing(s);
    setPlanId(s.plan_id || "");
    setPeriodOverride("");
  };

  const submit = async () => {
    try {
      const body = { plan_id: planId };
      if (periodOverride) body.period_days = Number(periodOverride);
      await api.put(`/master/tenants/${editing.tenant_id}/assign-plan`, body);
      toast.success("Paket atandı");
      setEditing(null);
      await load();
    } catch (err) {
      notifyError(err);
    }
  };

  const startExtend = (s) => {
    setExtending(s);
    setExtendDays("");
  };

  // Yalnızca pozitif tam sayı kabul edilir; asıl doğrulama backend'de.
  const parsedDays = Number(extendDays);
  const extendValid =
    Number.isInteger(parsedDays) && parsedDays > 0 && /^\d+$/.test(extendDays.trim());

  const submitExtend = async () => {
    if (!extendValid) {
      toast.error("Gün sayısı pozitif bir tam sayı olmalıdır");
      return;
    }
    setExtendSaving(true);
    try {
      const { data } = await api.put(
        `/master/tenants/${extending.tenant_id}/extend-subscription`,
        { days: parsedDays }
      );
      toast.success(
        `Süre ${parsedDays} gün uzatıldı. Yeni bitiş: ${fmtDate(data.expires_at)}`
      );
      setExtending(null);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setExtendSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6" data-testid="master-subscriptions-page">
      <div>
        <div className="label-eyebrow text-amber-600">Ödeme</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Abonelikler</h1>
        <p className="text-sm text-muted-foreground mt-2">
          İşletmelere paket atayın veya mevcut planlarını yönetin.
        </p>
      </div>

      {loading ? (
        <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-foreground text-background">
                <tr>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">İşletme</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Paket</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Fiyat</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Durum</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Başlangıç</th>
                  <th className="text-left px-4 py-3 label-eyebrow !text-gray-300">Bitiş</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.map((s) => (
                  <tr key={s.tenant_id} data-testid={`subscription-row-${s.tenant_id}`}>
                    <td className="px-4 py-3 font-semibold">{s.tenant_name}</td>
                    <td className="px-4 py-3">{s.plan_name || "—"}</td>
                    <td className="px-4 py-3 font-mono">{s.plan_price != null ? fmtMoney(s.plan_price, s.plan_currency || "TRY", { maximumFractionDigits: 0 }) : "—"}</td>
                    <td className="px-4 py-3">
                      <Badge className={`rounded-sm ${s.subscription_status === "active" ? "bg-green-600 hover:bg-green-600 text-white" : "bg-muted text-muted-foreground hover:bg-muted"}`}>
                        {s.subscription_status === "active" ? "AKTİF" : "YOK"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{fmtDate(s.started_at)}</td>
                    <td className="px-4 py-3 text-xs font-mono">{s.expires_at ? fmtDate(s.expires_at) : "Sınırsız"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => startEdit(s)} data-testid={`assign-plan-${s.tenant_id}`}>
                        Paket Ata
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-sm ml-2"
                        onClick={() => startExtend(s)}
                        data-testid={`extend-subscription-${s.tenant_id}`}
                      >
                        <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Süre Ata
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Paket Ata</DialogTitle>
            <DialogDescription>
              {editing?.tenant_name} işletmesine yeni paket atayın. Süre alanı boş bırakılırsa paketin varsayılan süresi kullanılır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="label-eyebrow">Paket</label>
              <Select value={planId} onValueChange={setPlanId}>
                <SelectTrigger className="rounded-sm" data-testid="assign-plan-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} — {p.price_try > 0 ? fmtMoney(p.price_try, p.currency || "TRY", { maximumFractionDigits: 0 }) : "Ücretsiz"} ({p.period_days || "∞"} gün)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="label-eyebrow">Süre Geçersiz Kıl (opsiyonel, gün)</label>
              <input
                type="number"
                value={periodOverride}
                onChange={(e) => setPeriodOverride(e.target.value)}
                className="w-full h-10 px-3 border border-input rounded-sm font-mono"
                placeholder="Boş bırak"
                data-testid="assign-period-override"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-sm">İptal</Button>
            <Button onClick={submit} disabled={!planId} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="confirm-assign">
              Ata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Süre Ata — mevcut bitiş tarihine gün ekler. Paket, başlangıç tarihi
          ve diğer abonelik bilgileri DEĞİŞMEZ. */}
      <Dialog open={!!extending} onOpenChange={(o) => !o && setExtending(null)}>
        <DialogContent className="rounded-sm" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Süre Ata</DialogTitle>
            <DialogDescription>
              <b>{extending?.tenant_name}</b> işletmesinin abonelik bitiş
              tarihine gün ekleyin. Paket ve başlangıç tarihi değişmez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="border border-border rounded-sm p-3">
                <div className="label-eyebrow">Mevcut Bitiş</div>
                <div className="font-mono font-bold mt-1" data-testid="extend-current-expiry">
                  {extending?.expires_at ? fmtDate(extending.expires_at) : "Sınırsız"}
                </div>
              </div>
              <div className="border border-border rounded-sm p-3">
                <div className="label-eyebrow">Yeni Bitiş (önizleme)</div>
                <div className="font-mono font-bold mt-1 text-amber-700" data-testid="extend-preview">
                  {extendValid
                    ? fmtDate(
                        new Date(
                          Math.max(
                            extending?.expires_at
                              ? new Date(extending.expires_at).getTime()
                              : Date.now(),
                            Date.now()
                          ) + parsedDays * 86400000
                        ).toISOString()
                      )
                    : "—"}
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Eklenecek Gün *</Label>
              <Input
                type="number"
                min="1"
                step="1"
                inputMode="numeric"
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                placeholder="Örn. 5"
                className="rounded-sm font-mono"
                data-testid="extend-days-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Yalnızca pozitif tam sayı. Bitiş tarihi geçmişse süre bugünden
                itibaren sayılır.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtending(null)} className="rounded-sm">
              İptal
            </Button>
            <Button
              onClick={submitExtend}
              disabled={!extendValid || extendSaving}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="confirm-extend"
            >
              {extendSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                `${extendValid ? parsedDays : 0} Gün Ekle`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
